import React, { useState, useEffect } from "react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
const DEFAULT_AFFILIATE_PERCENT = 30;
const DEFAULT_AFFILIATE_DURATION = 12;

const membershipPlans = [
  { id: 6, name: "Trải nghiệm", displayName: "Trải nghiệm (1 ngày)", monthlyPoints: 200 },
  { id: 1, name: "Cơ bản", displayName: "Cơ bản", monthlyPoints: 1000 },
  { id: 2, name: "Chuyên nghiệp", displayName: "Chuyên nghiệp", monthlyPoints: 2900 },
  { id: 3, name: "Cao Cấp", displayName: "Cao Cấp", monthlyPoints: 4300 },
  { id: 4, name: "VIP", displayName: "VIP", monthlyPoints: 7200 },
];

const getAdminHeaders = () => {
  const token = localStorage.getItem("userAccessToken");
  const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD || process.env.ADMIN_PASSWORD;
  return {
    "Content-Type": "application/json",
    ...(adminPassword ? { "X-Admin-Password": adminPassword } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

function formatDate(ts) {
  if (!ts) return "N/A";
  return new Date(ts).toLocaleDateString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh" });
}

function formatDateTime(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh" });
}

function StatusBadge({ status, activeLabel = "Hoạt động", errorLabel = "Lỗi", fallbackLabel }) {
  const label = status === "active" ? activeLabel : status === "error" ? errorLabel : (fallbackLabel || status || "Không rõ");
  const cls = status === "active"
    ? "bg-green-100 text-green-800"
    : status === "error" || status === "expired"
    ? "bg-red-100 text-red-800"
    : "bg-yellow-100 text-yellow-800";
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>;
}

function SectionHeader({ icon, title, count, color = "blue" }) {
  const colors = {
    blue: "from-blue-500 to-blue-600",
    cyan: "from-cyan-500 to-cyan-600",
    indigo: "from-indigo-500 to-indigo-600",
  };
  return (
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colors[color] || colors.blue} flex items-center justify-center text-white text-sm`}>
        {icon}
      </div>
      <h5 className="text-sm font-semibold text-gray-900">{title}</h5>
      {count !== undefined && (
        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{count}</span>
      )}
    </div>
  );
}

function ConnectionTable({ headers, children, emptyText = "Chưa có kết nối nào" }) {
  const isEmpty = !children || (Array.isArray(children) && children.length === 0);
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full bg-white">
        <thead>
          <tr className="bg-gray-50 text-gray-600 text-[10px] md:text-xs leading-normal">
            {headers.map((h, i) => (
              <th key={i} className="py-2 px-3 text-left font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="text-gray-700 text-[10px] md:text-xs divide-y divide-gray-100">
          {isEmpty ? (
            <tr><td colSpan={headers.length} className="py-6 px-3 text-center text-gray-400">{emptyText}</td></tr>
          ) : children}
        </tbody>
      </table>
    </div>
  );
}

const AdminUserDetail = ({ userId, onBack, onViewPosts, onViewAutoSettings }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("info");

  useEffect(() => {
    if (userId) fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, { headers: getAdminHeaders() });
      const result = await res.json();
      if (res.ok && result.success) setUser(result.data);
    } catch (err) {
      console.error("Lỗi tải chi tiết:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { id, connectedFanpages, connectedChannels, wordpressSites, fanpages, posts, ...userData } = user;
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: "PATCH",
        headers: getAdminHeaders(),
        body: JSON.stringify({ userData }),
      });
      const result = await res.json();
      if (result.success) {
        setEditMode(false);
        alert("Đã lưu thành công!");
      } else {
        alert("Lỗi: " + (result.message || "Không thể lưu"));
      }
    } catch (err) {
      alert("Lỗi kết nối: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "currentPoints") {
      setUser(prev => ({ ...prev, points: { ...prev.points, points: Number(value) || 0 } }));
    } else if (name === "membership") {
      const numVal = value ? parseInt(value, 10) : null;
      setUser(prev => ({
        ...prev,
        membership: numVal,
        // Auto-clear isTrial when switching away from trial plan (id=6)
        ...(numVal && numVal !== 6 ? { isTrial: false } : {}),
      }));
    } else if (name === "membershipExpiry") {
      setUser(prev => ({ ...prev, membershipExpiry: value ? new Date(value).getTime() : null }));
    } else {
      setUser(prev => ({ ...prev, [name]: value }));
    }
  };

  // Extract connections
  const facebookFanpages = user?.connectedFanpages
    ? (Array.isArray(user.connectedFanpages)
        ? user.connectedFanpages
        : Object.entries(user.connectedFanpages).map(([id, fp]) => ({ id: fp.id || id, ...fp })))
    : [];

  const zaloOAs = user?.connectedChannels?.zalo
    ? Object.entries(user.connectedChannels.zalo).map(([id, oa]) => ({ id: oa.id || id, ...oa }))
    : [];

  const wpSites = user?.wordpressSites
    ? (Array.isArray(user.wordpressSites)
        ? user.wordpressSites
        : Object.entries(user.wordpressSites).map(([id, site]) => ({ id, ...site })))
    : [];

  const legacyFanpages = user?.fanpages
    ? Object.entries(user.fanpages).map(([id, fp]) => ({ id, ...fp }))
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-500">Đang tải...</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500 mb-4">Không tìm thấy người dùng</p>
        <button onClick={onBack} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 text-sm">← Quay lại</button>
      </div>
    );
  }

  const tabs = [
    { key: "info", label: "Thông tin" },
    { key: "connections", label: `Kết nối (${facebookFanpages.length + zaloOAs.length + wpSites.length})` },
    { key: "affiliate", label: "Affiliate" },
  ];

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
            ← Quay lại
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-bold text-lg">
              {(user.name || user.email || "?").charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{user.name || user.displayName || "Không có tên"}</h2>
              <p className="text-sm text-gray-500">{user.email}</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {onViewPosts && (
            <button onClick={onViewPosts} className="px-3 py-2 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors">
              📋 Bài viết
            </button>
          )}
          {onViewAutoSettings && (
            <button onClick={onViewAutoSettings} className="px-3 py-2 text-sm bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors">
              ⚙️ Thiết lập tự động
            </button>
          )}
          {editMode ? (
            <>
              <button onClick={() => { setEditMode(false); fetchUser(); }} className="px-3 py-2 text-sm bg-gray-200 rounded-lg hover:bg-gray-300">Hủy</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {saving ? "Đang lưu..." : "Lưu"}
              </button>
            </>
          ) : (
            <button onClick={() => setEditMode(true)} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">Chỉnh sửa</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === tab.key ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Thông tin */}
      {activeTab === "info" && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="ID" value={user.id} disabled />
            <Field label="Tên" name="name" value={user.name || ""} onChange={handleChange} disabled={!editMode} />
            <Field label="Email" name="email" value={user.email || ""} onChange={handleChange} disabled={!editMode} />
            <Field label="Điện thoại" name="phone" value={user.phone || ""} onChange={handleChange} disabled={!editMode} />
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Membership</label>
              {editMode ? (
                <select name="membership" value={user.membership || ""} onChange={handleChange} className="w-full px-3 py-2 border rounded-md text-sm">
                  <option value="">Free</option>
                  {membershipPlans.map(p => <option key={p.id} value={p.id}>{p.displayName} - {p.monthlyPoints} điểm/tháng</option>)}
                </select>
              ) : (
                <input disabled value={user.membership ? (membershipPlans.find(p => p.id === parseInt(user.membership))?.displayName || `Plan ${user.membership}`) : "Free"} className="w-full px-3 py-2 border rounded-md bg-gray-50 text-gray-500 text-sm" />
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Hết hạn membership</label>
              <input type="datetime-local" name="membershipExpiry" disabled={!editMode}
                value={user.membershipExpiry ? new Date(user.membershipExpiry).toISOString().slice(0, 16) : ""}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md text-sm ${!editMode ? "bg-gray-50 text-gray-500" : ""}`}
              />
              {editMode && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {[{l:"+1 ngày",d:1},{l:"+7 ngày",d:7},{l:"+1 tháng",d:30},{l:"+1 năm",d:365}].map(b => (
                    <button key={b.l} type="button" onClick={() => { const d=new Date(); d.setDate(d.getDate()+b.d); setUser(u=>({...u,membershipExpiry:d.getTime()})); }}
                      className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600">{b.l}</button>
                  ))}
                </div>
              )}
            </div>
            <Field label="Ngày tạo" value={formatDateTime(user.createdAt)} disabled />
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Điểm hiện tại</label>
              <input type="number" name="currentPoints" disabled={!editMode}
                value={(user.points?.points) || 0} onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md text-sm ${!editMode ? "bg-gray-50 text-gray-500" : ""}`}
              />
              <div className="mt-1 text-xs text-gray-500">
                Điểm mua thêm: {user.points?.purchasedPoints || 0} | Tổng: {((user.points?.points)||0) + ((user.points?.purchasedPoints)||0)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Kết nối */}
      {activeTab === "connections" && (
        <div className="space-y-6">
          {/* Facebook Fanpages */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
            <SectionHeader icon="📘" title="Facebook Fanpages" count={facebookFanpages.length} color="blue" />
            <ConnectionTable headers={["ID Fanpage", "Tên", "Loại", "Trạng thái", "Ngày kết nối", "Cập nhật"]}>
              {facebookFanpages.map(fp => (
                <tr key={fp.id} className="hover:bg-gray-50">
                  <td className="py-2 px-3 max-w-[120px] truncate font-mono text-[10px]">{fp.id}</td>
                  <td className="py-2 px-3 max-w-[200px] truncate font-medium">{fp.name || "N/A"}</td>
                  <td className="py-2 px-3">{fp.category || "N/A"}</td>
                  <td className="py-2 px-3"><StatusBadge status={fp.tokenStatus || (fp.access_token ? "active" : "unknown")} /></td>
                  <td className="py-2 px-3">{formatDate(fp.connectedAt)}</td>
                  <td className="py-2 px-3">{formatDate(fp.lastRefreshed)}</td>
                </tr>
              ))}
            </ConnectionTable>
            {/* Legacy fanpages */}
            {legacyFanpages.length > 0 && facebookFanpages.length === 0 && (
              <div className="mt-3">
                <p className="text-xs text-gray-400 mb-2">Fanpages (legacy)</p>
                <ConnectionTable headers={["ID", "Tên"]}>
                  {legacyFanpages.map(fp => (
                    <tr key={fp.id}>
                      <td className="py-2 px-3 font-mono text-[10px]">{fp.id}</td>
                      <td className="py-2 px-3">{fp.name || "N/A"}</td>
                    </tr>
                  ))}
                </ConnectionTable>
              </div>
            )}
          </div>

          {/* Zalo OA */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
            <SectionHeader icon="💬" title="Zalo OA" count={zaloOAs.length} color="cyan" />
            <ConnectionTable headers={["OA ID", "Tên OA", "Danh mục", "Trạng thái", "Ngày kết nối", "Cập nhật"]} emptyText="Chưa kết nối Zalo OA nào">
              {zaloOAs.map(oa => (
                <tr key={oa.id} className="hover:bg-gray-50">
                  <td className="py-2 px-3 font-mono text-[10px]">{oa.accountId || oa.id}</td>
                  <td className="py-2 px-3 font-medium">
                    <div className="flex items-center gap-2">
                      {oa.avatar && <img src={oa.avatar} alt="" className="w-6 h-6 rounded-full" />}
                      {oa.name || "N/A"}
                    </div>
                  </td>
                  <td className="py-2 px-3">{oa.category || "N/A"}</td>
                  <td className="py-2 px-3"><StatusBadge status={oa.tokenStatus} /></td>
                  <td className="py-2 px-3">{formatDate(oa.connectedAt)}</td>
                  <td className="py-2 px-3">{formatDate(oa.lastRefreshed)}</td>
                </tr>
              ))}
            </ConnectionTable>
          </div>

          {/* WordPress Sites */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
            <SectionHeader icon="🌐" title="WordPress Sites" count={wpSites.length} color="indigo" />
            <ConnectionTable headers={["ID", "Tên site", "URL", "Trạng thái", "Ngày kết nối"]} emptyText="Chưa kết nối WordPress nào">
              {wpSites.map(site => (
                <tr key={site.id} className="hover:bg-gray-50">
                  <td className="py-2 px-3 font-mono text-[10px] max-w-[100px] truncate">{site.id}</td>
                  <td className="py-2 px-3 font-medium">{site.name || site.siteName || "N/A"}</td>
                  <td className="py-2 px-3 max-w-[200px] truncate">
                    {site.url || site.siteUrl ? (
                      <a href={site.url || site.siteUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                        {site.url || site.siteUrl}
                      </a>
                    ) : "N/A"}
                  </td>
                  <td className="py-2 px-3"><StatusBadge status={site.status || "active"} /></td>
                  <td className="py-2 px-3">{formatDate(site.connectedAt || site.createdAt)}</td>
                </tr>
              ))}
            </ConnectionTable>
          </div>

          {/* Summary */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
            <h5 className="text-xs font-semibold text-gray-500 uppercase mb-3">Tổng quan kết nối</h5>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{facebookFanpages.length}</div>
                <div className="text-xs text-gray-500">Facebook</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-cyan-600">{zaloOAs.length}</div>
                <div className="text-xs text-gray-500">Zalo OA</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">{wpSites.length}</div>
                <div className="text-xs text-gray-500">WordPress</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Affiliate */}
      {activeTab === "affiliate" && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard label="Signup hoàn tất" value={user.affiliateStats?.completedSignups || 0} bg="bg-blue-50" text="text-blue-600" />
            <StatCard label="Signup chờ xử lý" value={user.affiliateStats?.pendingSignups || 0} bg="bg-yellow-50" text="text-yellow-600" />
            <StatCard label="Tổng doanh số" value={`${(user.affiliateStats?.totalSales || 0).toLocaleString("vi-VN")}đ`} bg="bg-emerald-50" text="text-emerald-600" />
            <StatCard label="Tổng hoa hồng" value={`${(user.affiliateStats?.totalEarnings || 0).toLocaleString("vi-VN")}đ`} bg="bg-purple-50" text="text-purple-600" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">% hoa hồng</label>
              <input type="number" name="affiliatePercent" min="0" max="100" disabled={!editMode}
                value={user.affiliatePercent ?? DEFAULT_AFFILIATE_PERCENT} onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md text-sm ${!editMode ? "bg-gray-50 text-gray-500" : ""}`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Thời hạn (tháng)</label>
              <input type="number" name="affiliateDuration" min="1" disabled={!editMode}
                value={user.affiliateDuration ?? DEFAULT_AFFILIATE_DURATION} onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md text-sm ${!editMode ? "bg-gray-50 text-gray-500" : ""}`}
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-700 mb-1">Link affiliate</label>
            <div className="flex gap-2">
              <input disabled value={`${window.location.origin}/?ref=${user.id}`} className="w-full px-3 py-2 border rounded-md bg-gray-50 text-gray-500 text-sm" />
              <button type="button" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/?ref=${user.id}`); alert("Đã copy!"); }}
                className="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300">Copy</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function Field({ label, value, disabled = true, name, onChange, ...props }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <input type="text" name={name} value={value || ""} disabled={disabled} onChange={onChange}
        className={`w-full px-3 py-2 border rounded-md text-sm ${disabled ? "bg-gray-50 text-gray-500" : "focus:ring-blue-500 focus:border-blue-500"}`}
        {...props}
      />
    </div>
  );
}

function StatCard({ label, value, bg, text }) {
  return (
    <div className={`${bg} rounded-lg p-3`}>
      <div className={`text-xs ${text} uppercase`}>{label}</div>
      <div className={`text-xl font-bold ${text.replace("600", "900")} mt-1`}>{value}</div>
    </div>
  );
}

export default AdminUserDetail;
